import { uuid } from 'vue-uuid'
import cloneDeep from 'lodash.clonedeep'
import { Reposition } from '@/helpers/storeHelper'
import { fromScssToCss } from '@/helpers/filtersHelper.js'
import Vue from 'vue'

const getPrefix = type => {
  const widgetPrefix = 'widget/'

  if (!type.startsWith(widgetPrefix)) {
    return type.toLowerCase()
  }

  return type.replace(widgetPrefix, '').toLowerCase()
}

const recompileCss = async elements => {
  return await Promise.all(
    elements.map(async e => {
      if (!e.options) {
        return e
      }

      const { id, options, type } = e
      const prefix = getPrefix(type)

      if (e.options.hasOwnProperty('style')) {
        e.options.style.css = await fromScssToCss(`.${prefix}_${id} { ${options.style.scss} }`)

        return e
      }

      const [cssLg, cssMd, cssSm] = await Promise.all([
        fromScssToCss(`.${prefix}_${id} { ${options.lg.style.scss} }`),
        fromScssToCss(`.${prefix}_${id} { ${options.md.style.scss} }`),
        fromScssToCss(`.${prefix}_${id} { ${options.sm.style.scss} }`),
      ])

      e.options.lg.style.css = cssLg
      e.options.md.style.css = cssMd
      e.options.sm.style.css = cssSm
      return e
    })
  )
}

const newId = type => {
  let prefix = 'W-'
  if (type === 'Section') {
    prefix = 'S-'
  } else if (type === 'Panel') {
    prefix = 'P-'
  }
  return prefix + uuid.v1()
}

const updateIdsAndPath = (items, item, path, newIds) => {
  if (newIds) {
    item.id = newId(item.type)
  }
  if (item.type !== 'Section') {
    item.path = path
  }
  if (item.type === 'Section' || item.type === 'Panel') {
    item.children = item.children.map(c =>
      updateIdsAndPath(
        items,
        items.find(i => i.id === c),
        [...path, item.id],
        newIds
      )
    )
  }
  if (item.type === 'widget/Form') {
    item.options.items = item.options.items.map(item => ({ ...item, id: uuid.v1() }))
  }
  return item.id
}

export default {
  async batchAdd(
    { state, commit, dispatch, getters },
    { id = null, parentId = null, elements = [], stack = 'undo', stackToClear = 'redo' } = {}
  ) {
    if (!elements.length) {
      return
    }

    elements = cloneDeep(elements)

    const root = elements[0]
    const isSection = root.type === 'Section'

    if ((isSection && parentId) || (!isSection && !parentId)) {
      return false
    }

    const parent = getters.objectById(parentId)

    const canAdd = isSection || (parent && !parent.children.some(c => c.startsWith('W-')))

    if (canAdd) {
      const path = isSection ? [] : [...(parent.path || []), parentId]

      updateIdsAndPath(elements, root, path, id === null)
      elements = await recompileCss(elements)

      if (!id) {
        const position = isSection ? getters.containers(['Section']).length : parent.children.length
        root.options.sm.position = position
        root.options.md.position = position
        root.options.lg.position = position
      }

      const siblings = isSection
        ? state.landing.filter(l => l.type === 'Section')
        : parent.children.map(c => getters.objectById(c))

      const reposition = Reposition(siblings, root.options, (crr, ref) => crr >= ref, 1)

      commit('batchAdd', { parentId, elements })
      commit('reposition', reposition)

      dispatch(
        'history/pushToStack',
        {
          stack,
          action: 'landing/batchRemove',
          params: { id: root.id },
          stackToClear,
        },
        { root: true }
      )

      Vue.nextTick(() => setTimeout(() => dispatch('ui/scrollIntoView', root.id, { root: true })))
    }
  },

  batchRemove({ state, commit, dispatch, getters }, { id = null, stack = 'undo', stackToClear = 'redo' } = {}) {
    if (!id) {
      return
    }

    const root = getters.objectById(id)
    const isSection = root.type === 'Section'
    const parentId = isSection ? null : root.path[root.path.length - 1]
    const parent = getters.objectById(parentId)

    const elements = getters.descendants(id)

    const siblings = isSection
      ? state.landing.filter(l => l.type === 'Section')
      : parent.children.map(c => getters.objectById(c))

    const reposition = Reposition(siblings, root.options, (crr, ref) => crr > ref, -1)

    if (getters.isActive(id)) {
      dispatch('setActive')
    }

    commit('batchRemove', { parentId, elements: [id, ...elements.map(el => el.id)] })
    commit('reposition', reposition)

    dispatch(
      'history/pushToStack',
      {
        stack,
        action: 'landing/batchAdd',
        params: { id, parentId, elements: [root, ...elements] },
        stackToClear,
      },
      { root: true }
    )
  },
}
